/* WAVY-JADE-0926: Jade filter rail (sections/wavy-jade-rail.liquid). Rewrite of the WAVY-JADE-0925 rail script.
   Progressive enhancement over plain links and <details> pills.
   Pills: one panel open at a time. Opening commits the closed state with a forced reflow (never rAF) and then adds
   .is-open; closing removes it and drops [open] after the exit transition. Escape closes and returns focus to the
   pill, an outside press or focus leaving closes, arrow keys / Home / End move through the options. Under 750px
   the panels are bottom sheets over a scrim and the page scroll is locked while one is up.
   Swaps: every rail link (tabs included; the three jade collections share templates/collection.json, so their
   section ids match) fetches ?sections=<banner>,<rail>,<grid> in one request, and falls back to parsing the full
   page by DOM if those ids don't resolve. The tabs are never re-rendered, so the ink slides all the way; the pills
   and sort pill are replaced, the count updates inside its live region, and the grid crossfades: out over 140ms,
   then the new cards stagger in 40ms apart, with the grid height locked until they land. History push + popstate.
   Hover intent and pointer press prefetch the target, so most swaps start from cache.
   Dispatches "wj:grid-updated" on document after every swap. */
(() => {
  'use strict';
  if (window.wjRailInstalled) return;
  window.wjRailInstalled = true;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const sheetQuery = window.matchMedia('(max-width: 749px)');
  const PANEL_OUT_MS = 240;
  const SCRIM_OUT_MS = 300;
  const GRID_OUT_MS = 140;
  const ENTER_MS = 360;
  const STAGGER_MS = 40;
  const STAGGER_MAX = 11;
  const CACHE_MS = 60000;

  const closeTimers = new WeakMap();
  const cache = new Map();
  let openPill = null;
  let sheetTimer = 0;
  let settleTimer = 0;
  let intentTimer = 0;
  let navToken = 0;
  let rendered = window.location.pathname + window.location.search;

  const railEl = () => document.querySelector('[data-wj-rail]');
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const animated = () => !reduced.matches;

  function handleOf(pathname) {
    const parts = pathname.split('/').filter(Boolean);
    const i = parts.indexOf('collections');
    return i === -1 ? null : parts[i + 1] || null;
  }

  function tabHandles(rail) {
    return Array.from(rail.querySelectorAll('.wj-rail__tab')).map((tab) =>
      handleOf(new URL(tab.href, window.location.href).pathname)
    );
  }

  function inScope(rail, url) {
    return url.origin === window.location.origin && tabHandles(rail).indexOf(handleOf(url.pathname)) !== -1;
  }

  function sectionIdOf(el) {
    const section = el && el.closest('.shopify-section');
    return section ? section.id.replace(/^shopify-section-/, '') : null;
  }

  // Tell facets.js which params are already on screen so its own popstate handler leaves the grid alone.
  function syncFacets(searchParams) {
    try {
      if (typeof FacetFiltersForm !== 'undefined') FacetFiltersForm.searchParamsPrev = searchParams;
    } catch (e) {
      /* facets.js not on this page */
    }
  }

  /* ---------------------------------------------------------------- tabs + ink */

  function placeInk(rail, tab) {
    const nav = rail && rail.querySelector('[data-wj-rail-tabs]');
    const target = tab || (nav && nav.querySelector('[aria-current="page"]'));
    if (!nav || !target) return;
    nav.style.setProperty('--wj-ink-x', target.offsetLeft + 'px');
    nav.style.setProperty('--wj-ink-w', String(target.offsetWidth));
    nav.classList.add('has-ink');
  }

  function markTab(rail, tab) {
    rail.querySelectorAll('.wj-rail__tab').forEach((link) => {
      if (link === tab) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    placeInk(rail, tab);
  }

  /* ---------------------------------------------------------------- pills */

  const isOpen = (pill) => pill.classList.contains('is-open');
  const optionsOf = (pill) => Array.from(pill.querySelectorAll('[data-wj-panel] a.wj-opt'));

  function focusOption(pill, which) {
    const list = optionsOf(pill);
    if (!list.length) return;
    let target = list[0];
    if (which === 'last') target = list[list.length - 1];
    else if (which === 'selected') target = pill.querySelector('[data-wj-panel] a.wj-opt.is-selected') || list[0];
    target.focus();
  }

  function lockPage(on) {
    document.documentElement.classList.toggle('wj-rail-lock', on);
  }

  function open(pill, focusWhich) {
    const rail = pill.closest('[data-wj-rail]');
    const details = pill.querySelector('.wj-pill__d');
    if (!rail || !details) return;
    if (openPill && openPill !== pill) close(openPill, false);
    clearTimeout(closeTimers.get(pill));
    details.open = true;
    const sheet = sheetQuery.matches;
    if (sheet) {
      clearTimeout(sheetTimer);
      rail.classList.add('has-sheet');
      lockPage(true);
    }
    pill.getBoundingClientRect(); // commit the closed panel so the entrance transitions
    pill.classList.add('is-open');
    if (sheet) rail.classList.add('is-sheet-open');
    openPill = pill;
    if (focusWhich) focusOption(pill, focusWhich);
  }

  function close(pill, restoreFocus) {
    if (!pill) return;
    const rail = pill.closest('[data-wj-rail]');
    const details = pill.querySelector('.wj-pill__d');
    pill.classList.remove('is-open');
    if (openPill === pill) openPill = null;
    const outMs = animated() ? PANEL_OUT_MS : 0;
    clearTimeout(closeTimers.get(pill));
    const settle = () => {
      if (details && !pill.classList.contains('is-open')) details.open = false;
    };
    if (outMs) closeTimers.set(pill, setTimeout(settle, outMs));
    else settle();

    if (rail && rail.classList.contains('has-sheet')) {
      rail.classList.remove('is-sheet-open');
      clearTimeout(sheetTimer);
      const endSheet = () => {
        if (openPill) return;
        rail.classList.remove('has-sheet');
        lockPage(false);
      };
      if (outMs) sheetTimer = setTimeout(endSheet, SCRIM_OUT_MS);
      else endSheet();
    }
    if (restoreFocus) {
      const summary = pill.querySelector('.wj-pill__btn');
      if (summary) summary.focus({ preventScroll: true });
    }
  }

  /* ---------------------------------------------------------------- fetching */

  async function request(url) {
    const rail = railEl();
    const grid = document.getElementById('product-grid');
    const railId = rail && rail.dataset.sectionId;
    const gridId = grid && grid.dataset.id;
    const ids = [sectionIdOf(document.querySelector('.collection-hero')), railId, gridId].filter(Boolean);
    let html = null;
    if (railId && gridId) {
      const sectionsUrl = new URL(url.href);
      sectionsUrl.searchParams.set('sections', ids.join(','));
      // WAVY-JADE-0925: no Accept: application/json header, it makes Shopify return collection JSON instead of sections
      const response = await fetch(sectionsUrl.href, { credentials: 'same-origin' });
      if (response.ok) {
        const data = await response.json().catch(() => null);
        if (data && data[railId] && data[gridId]) html = ids.map((id) => data[id] || '').join('');
      }
    }
    if (html === null) {
      // Different template on the target page: read the full document and find the sections by DOM instead.
      const response = await fetch(url.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('HTTP ' + response.status);
      html = await response.text();
    }
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function load(url) {
    const key = url.pathname + url.search;
    const hit = cache.get(key);
    if (hit && Date.now() - hit.at < CACHE_MS) return hit.promise;
    const promise = request(url);
    promise.catch(() => {
      if (cache.get(key) && cache.get(key).promise === promise) cache.delete(key);
    });
    cache.set(key, { at: Date.now(), promise });
    if (cache.size > 12) cache.delete(cache.keys().next().value);
    return promise;
  }

  function prefetch(link) {
    const rail = link.closest('[data-wj-rail]');
    const url = new URL(link.href, window.location.href);
    if (!rail || !inScope(rail, url)) return;
    if (url.pathname + url.search === window.location.pathname + window.location.search) return;
    load(url).catch(() => {});
  }

  function pick(doc) {
    const rail = doc.querySelector('[data-wj-rail]');
    const grid = doc.getElementById('product-grid');
    const hero = doc.querySelector('.collection-hero');
    return {
      rail,
      grid: grid && grid.closest('.shopify-section'),
      hero: hero && hero.closest('.shopify-section'),
    };
  }

  /* ---------------------------------------------------------------- swapping */

  function titleOf(section) {
    const title = section && section.querySelector('.collection-hero__title');
    return title ? title.textContent.replace(/\s+/g, ' ').trim() : '';
  }

  function swapRail(rail, next) {
    ['[data-wj-rail-pills]', '[data-wj-rail-sort]'].forEach((selector) => {
      const current = rail.querySelector(selector);
      const incoming = next.querySelector(selector);
      if (current && incoming) current.replaceWith(document.importNode(incoming, true));
    });
    const count = rail.querySelector('[data-wj-rail-count]');
    const nextCount = next.querySelector('[data-wj-rail-count]');
    const text = nextCount ? nextCount.textContent.replace(/\s+/g, ' ').trim() : '';
    if (count && text && count.textContent.trim() !== text) count.textContent = text;
    const current = next.querySelector('.wj-rail__tab[aria-current="page"]');
    const tab = current && rail.querySelector('.wj-rail__tab[data-wj-key="' + current.dataset.wjKey + '"]');
    if (tab) markTab(rail, tab);
  }

  function focusAfter(rail, key) {
    if (!key) return;
    const active = document.activeElement;
    if (active && active !== document.body && !rail.contains(active)) return; // shopper already moved on
    const safe = key.replace(/["\\]/g, '');
    const target = rail.querySelector('[data-wj-key="' + safe + '"]') || rail.querySelector('.wj-pill__btn');
    if (target) target.focus({ preventScroll: true });
  }

  async function go(url, options) {
    const opts = options || {};
    const rail = railEl();
    const grid = document.getElementById('product-grid');
    const gridSection = grid && grid.closest('.shopify-section');
    if (!rail || !gridSection) {
      window.location.assign(url.href);
      return;
    }
    const hero = document.querySelector('.collection-hero');
    const heroSection = hero && hero.closest('.shopify-section');
    const switching = handleOf(url.pathname) !== handleOf(window.location.pathname);
    const motion = animated();
    const token = ++navToken;
    const hadPanel = Boolean(openPill);
    if (openPill) close(openPill, false);

    clearTimeout(settleTimer);
    gridSection.classList.remove('wj-swap-in');
    if (heroSection) heroSection.classList.remove('wj-swap-in');
    gridSection.style.minHeight = gridSection.offsetHeight + 'px';
    if (motion) {
      gridSection.classList.add('wj-swap-out');
      if (switching && heroSection) heroSection.classList.add('wj-swap-out');
    }
    rail.setAttribute('aria-busy', 'true');
    gridSection.setAttribute('aria-busy', 'true');

    let doc = null;
    try {
      const results = await Promise.all([load(url), wait(motion ? (hadPanel ? PANEL_OUT_MS : GRID_OUT_MS) : 0)]);
      doc = results[0];
    } catch (error) {
      doc = null;
    }
    if (token !== navToken) return; // a newer choice took over
    const next = doc ? pick(doc) : {};
    if (!next.rail || !next.grid) {
      window.location.assign(url.href); // any failure falls back to a normal page load
      return;
    }

    if (opts.push !== false) {
      window.history.pushState({ wjRail: true, searchParams: url.search.slice(1) }, '', url.pathname + url.search);
    }
    rendered = window.location.pathname + window.location.search;
    syncFacets(window.location.search.slice(1));

    swapRail(rail, next.rail);
    if (switching && heroSection && next.hero) {
      const before = titleOf(heroSection);
      heroSection.innerHTML = next.hero.innerHTML;
      const after = titleOf(heroSection);
      if (before && after && before !== after) document.title = document.title.replace(before, after);
    }
    gridSection.innerHTML = next.grid.innerHTML;
    rail.removeAttribute('aria-busy');
    gridSection.removeAttribute('aria-busy');
    gridSection.classList.remove('wj-swap-out');
    if (heroSection) heroSection.classList.remove('wj-swap-out');

    if (motion) {
      gridSection.querySelectorAll('#product-grid > li').forEach((item, i) => {
        item.style.setProperty('--wj-i', String(Math.min(i, STAGGER_MAX)));
      });
      gridSection.classList.add('wj-swap-in');
      if (switching && heroSection) heroSection.classList.add('wj-swap-in');
    }
    settleTimer = setTimeout(
      () => {
        gridSection.classList.remove('wj-swap-in');
        if (heroSection) heroSection.classList.remove('wj-swap-in');
        gridSection.style.minHeight = '';
      },
      motion ? ENTER_MS + STAGGER_MS * STAGGER_MAX + 60 : 0
    );

    focusAfter(rail, opts.focus);
    const newGrid = document.getElementById('product-grid');
    document.dispatchEvent(
      new CustomEvent('wj:grid-updated', { detail: { url: url.href, sectionId: newGrid ? newGrid.dataset.id : null } })
    );
  }

  /* ---------------------------------------------------------------- events */

  document.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const summary = target.closest('[data-wj-rail] .wj-pill__btn');
    if (summary) {
      event.preventDefault(); // the rail owns the open state (animated close)
      const pill = summary.closest('[data-wj-pill]');
      if (isOpen(pill)) close(pill, false);
      else open(pill, event.detail === 0 ? 'selected' : null); // keyboard activation lands on the options
      return;
    }

    const closer = target.closest('[data-wj-rail] [data-wj-pill-close]');
    if (closer) {
      close(closer.closest('[data-wj-pill]'), true);
      return;
    }

    const link = target.closest('[data-wj-rail] a[data-wj-rail-link]');
    if (!link || event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const rail = link.closest('[data-wj-rail]');
    const url = new URL(link.href, window.location.href);
    if (!inScope(rail, url)) return; // full navigation
    event.preventDefault();
    if (link.classList.contains('wj-rail__tab')) markTab(rail, link);
    if (url.pathname + url.search === window.location.pathname + window.location.search) {
      if (openPill) close(openPill, true);
      return;
    }
    go(url, { focus: link.dataset.wjFocus || null });
  });

  document.addEventListener(
    'keydown',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (event.key === 'Escape' && openPill) {
        event.preventDefault();
        event.stopPropagation(); // only the panel closes, nothing underneath reacts
        close(openPill, true);
        return;
      }

      const summary = target.closest('[data-wj-rail] .wj-pill__btn');
      if (summary && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        const pill = summary.closest('[data-wj-pill]');
        const which = event.key === 'ArrowUp' ? 'last' : 'selected';
        if (isOpen(pill)) focusOption(pill, which === 'last' ? 'last' : 'first');
        else open(pill, which);
        return;
      }

      if (!openPill || !openPill.contains(target) || !target.closest('[data-wj-panel]')) return;
      const list = optionsOf(openPill);
      if (!list.length) return;
      const i = list.indexOf(target.closest('a.wj-opt'));
      let next = -1;
      if (event.key === 'ArrowDown') next = i < 0 ? 0 : (i + 1) % list.length;
      else if (event.key === 'ArrowUp') next = i < 0 ? list.length - 1 : (i - 1 + list.length) % list.length;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = list.length - 1;
      if (next < 0) return;
      event.preventDefault();
      list[next].focus();
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (openPill && !openPill.contains(event.target)) close(openPill, false);
      const link = event.target.closest && event.target.closest('[data-wj-rail] a[data-wj-rail-link]');
      if (link && event.button === 0) prefetch(link); // a press is ~100ms ahead of its click
    },
    true
  );

  document.addEventListener('focusin', (event) => {
    if (openPill && !openPill.contains(event.target)) close(openPill, false);
  });

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const link = event.target.closest && event.target.closest('[data-wj-rail] a[data-wj-rail-link]');
    if (!link) return;
    clearTimeout(intentTimer);
    intentTimer = setTimeout(() => prefetch(link), 90);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.target.closest && event.target.closest('[data-wj-rail] a[data-wj-rail-link]')) clearTimeout(intentTimer);
  });

  // Registered before facets.js (this script sits in the section above the grid), so it runs first.
  window.addEventListener('popstate', () => {
    const rail = railEl();
    if (!rail) return;
    syncFacets(window.location.search.slice(1));
    if (window.location.pathname + window.location.search === rendered) return;
    const url = new URL(window.location.href);
    if (!inScope(rail, url)) {
      window.location.reload();
      return;
    }
    const tab = rail.querySelector('.wj-rail__tab[data-wj-key="tab-' + handleOf(url.pathname) + '"]');
    if (tab) markTab(rail, tab);
    go(url, { push: false });
  });

  const onSheetChange = () => {
    if (openPill) close(openPill, false);
  };
  if (sheetQuery.addEventListener) sheetQuery.addEventListener('change', onSheetChange);

  function init() {
    const rail = railEl();
    if (!rail) return;
    rail.classList.add('wj-rail--js');
    placeInk(rail);
    rail.getBoundingClientRect(); // commit the resting ink position before transitions switch on
    rail.classList.add('wj-rail--ready');
  }

  /* WAVY-CRO-1001C: re-measures after web fonts land (or a resize) move the underline INSTANTLY. It used to slide
     420ms to the new tab width a moment after load, which read as a strange snap. Only clicks animate it now. */
  function placeInkNow(rail) {
    if (!rail) return;
    const ready = rail.classList.contains('wj-rail--ready');
    if (ready) rail.classList.remove('wj-rail--ready');
    placeInk(rail);
    rail.getBoundingClientRect();
    if (ready) rail.classList.add('wj-rail--ready');
  }

  let resizeTimer = 0;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => placeInkNow(railEl()), 120);
  });
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => placeInkNow(railEl()));

  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.querySelector && event.target.querySelector('[data-wj-rail]')) init();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
